import { useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/cn";
import { errorMessage } from "@/lib/api";
import { useConfirm, useToast, type ConfirmRequest } from "@/stores/feedback";
import { Button, Eyebrow, Input } from "./ui";
import { CloseIcon } from "./icons";

/** Open overlays, oldest first — only the last one answers Escape. */
const overlayStack: object[] = [];

/** Lock page scroll and close on Escape while an overlay is up. */
function useOverlay(open: boolean, onClose: () => void) {
  useEffect(() => {
    if (!open) return;
    const me = {};
    overlayStack.push(me);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && overlayStack[overlayStack.length - 1] === me) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      overlayStack.splice(overlayStack.indexOf(me), 1);
      if (overlayStack.length === 0) document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);
}

/**
 * Slide-over at the desk, full sheet on the phone. Same content, same order.
 */
export function Sheet({
  open,
  onClose,
  children,
  label,
  width = 470,
}: {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  label: string;
  width?: number;
}) {
  useOverlay(open, onClose);
  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-[70] flex items-end justify-end min-[900px]:items-stretch" role="dialog" aria-modal="true" aria-label={label}>
      <button className="animate-fade-k absolute inset-0 bg-[rgba(13,16,20,.55)]" aria-label="Close" onClick={onClose} />
      <div
        className={cn(
          "relative flex w-full flex-col overflow-hidden bg-surface shadow-float",
          "animate-rise-k max-h-[92dvh] rounded-t-xl border-t border-line-strong min-[900px]:animate-slide-k min-[900px]:max-h-none min-[900px]:rounded-t-none min-[900px]:rounded-l-xl min-[900px]:border-l min-[900px]:border-t-0",
        )}
        style={{ maxWidth: typeof window !== "undefined" && window.innerWidth >= 900 ? width : undefined }}
      >
        <div className="flex justify-center py-2 min-[900px]:hidden" aria-hidden>
          <span className="h-1 w-[42px] rounded-full bg-line-strong" />
        </div>
        {children}
      </div>
    </div>,
    document.body,
  );
}

export function SheetClose({ onClose }: { onClose: () => void }) {
  return (
    <button onClick={onClose} aria-label="Close" className="flex size-10 shrink-0 items-center justify-center rounded-full border border-line-strong bg-sunk hover:bg-canvas">
      <CloseIcon size={18} />
    </button>
  );
}

/** Centred dialog: the one surface that takes the large kadil. */
export function Dialog({ open, onClose, children, label }: { open: boolean; onClose: () => void; children: ReactNode; label: string }) {
  useOverlay(open, onClose);
  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 sm:p-6" role="dialog" aria-modal="true" aria-label={label}>
      <button className="animate-fade-k absolute inset-0 bg-[rgba(13,16,20,.5)]" aria-label="Close" onClick={onClose} />
      <div className="animate-rise-k kadil-lg relative max-h-[90dvh] w-full max-w-[520px] overflow-y-auto rounded-xl border border-line-strong bg-surface shadow-float">{children}</div>
    </div>,
    document.body,
  );
}

/**
 * The one confirm dialog. Eyebrow in rhododendron, a Fraunces question, the
 * consequence in plain words, then "Keep it" against a verb that names the act.
 */
export function ConfirmHost() {
  const { request, seq, close } = useConfirm();
  if (!request) return null;
  return <ConfirmPanel key={seq} request={request} close={close} />;
}

function ConfirmPanel({ request, close }: { request: ConfirmRequest; close: () => void }) {
  const [busy, setBusy] = useState(false);
  const [typed, setTyped] = useState("");
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (request.typeToConfirm) setTimeout(() => inputRef.current?.focus(), 50);
  }, [request.typeToConfirm]);

  const blocked = !!request.typeToConfirm && typed.trim() !== request.typeToConfirm;

  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      await request.run();
      close();
    } catch (e) {
      setError(errorMessage(e));
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={() => !busy && close()} label={request.title}>
      <div className="px-6 pb-4 pt-6">
        <Eyebrow tone="rhodo" className="mb-2">
          Confirm · cannot be undone
        </Eyebrow>
        <div className="font-display text-[27px] font-semibold leading-[1.2]">{request.title}</div>
        <p className="mt-2.5 text-[15px] leading-[1.6] text-muted">{request.body}</p>
        {request.typeToConfirm && (
          <div className="mt-4">
            <div className="mb-1.5 text-[13px] text-muted">
              Type <span className="font-mono font-semibold text-ink">{request.typeToConfirm}</span> to confirm
            </div>
            <Input ref={inputRef} mono value={typed} onChange={(e) => setTyped(e.target.value)} inputMode="numeric" />
          </div>
        )}
        {error && <div className="mt-3 rounded-md bg-[color-mix(in_srgb,var(--rhododendron)_9%,var(--surface))] px-3.5 py-2.5 text-[13.5px]">{error}</div>}
      </div>
      <div className="flex flex-wrap justify-end gap-2.5 border-t border-line bg-sunk px-6 pb-5 pt-4">
        <Button size="lg" className="bg-surface" onClick={close} disabled={busy}>
          {request.keep ?? "Keep it"}
        </Button>
        <Button size="lg" variant={request.tone === "primary" ? "primary" : "danger"} onClick={run} disabled={blocked} loading={busy}>
          {request.verb}
        </Button>
      </div>
    </Dialog>
  );
}

const toneBg = { pine: "bg-pine", brass: "bg-brass !text-brass-ink", rhodo: "bg-rhodo !text-[#FFFDF8]", slate: "bg-slate" } as const;

export function ToastHost() {
  const t = useToast((s) => s.toast);
  const dismiss = useToast((s) => s.dismiss);
  if (!t) return null;
  return createPortal(
    <div
      key={t.id}
      role="status"
      aria-live="polite"
      onClick={dismiss}
      // Lifts above a board's bulk bar while one is showing.
      style={{ marginBottom: "var(--toast-lift, 0px)" }}
      className={cn(
        "animate-rise-sm-k fixed bottom-[calc(26px+env(safe-area-inset-bottom))] left-1/2 z-[90] flex max-w-[calc(100vw-32px)] -translate-x-1/2 items-center gap-3 rounded-full py-3 pl-3 pr-5 text-on-fill shadow-float max-[899px]:bottom-[calc(96px+env(safe-area-inset-bottom))]",
        toneBg[t.tone],
      )}
    >
      <span className="rounded-full bg-[rgba(0,0,0,.16)] px-2.5 py-0.5 font-mono text-[11px] tracking-[.1em]">{t.tag}</span>
      <span className="text-[14px] font-semibold">{t.text}</span>
      {t.action && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            dismiss();
            t.action!.run();
          }}
          className="-my-1 ml-1 rounded-full bg-[rgba(255,255,255,.2)] px-3 py-1 text-[13px] font-bold hover:bg-[rgba(255,255,255,.3)]"
        >
          {t.action.label}
        </button>
      )}
    </div>,
    document.body,
  );
}